import { getIBPulseFeed, recordIBPulseEngagement } from '@/api/ibModule';

// Sources a feed event can carry, in chip display order. 'placeholder' events
// (synthetic, from the placeholder feeder) are rendered but get no chip.
export const PULSE_SOURCES = ['news', 'usda_release', 'price_move', 'advisor_manual'];

function dedupeById(existing, incoming) {
  const seen = new Set(existing.map((event) => event.id));
  return (incoming || []).filter((event) => !seen.has(event.id));
}

export default {
  namespaced: true,

  state: () => ({
    // Lazy-loaded, newest-first. Preloaded with today's events (or the latest
    // few) and grown backwards by "Show previous events"; new events prepend on
    // the live poll. Accumulation lives here in Vuex, so it survives tab
    // switches and resets only on a full page reload (fresh store).
    events: [],
    freshHasMore: false,
    catchUp: { events: [], hasMore: false },
    loading: false,
    loaded: false,
    error: false,
    // Source segment chips: the sources currently suppressed (hidden). Empty
    // means "show everything" — an advisor toggles a category off temporarily
    // without dismissing individual items, and unknown sources (e.g. the
    // placeholder feeder) stay filterable because the check is an exclusion.
    suppressedSources: [],
    // Session-local dismissals — the upstream service only keeps aggregate
    // dismiss counters, so hiding is per-tab, not persisted.
    dismissedIds: [],
    // Events blasted in this session, so a send moves the item into the
    // catch-up table immediately; the next poll reconciles with the server,
    // which owns the durable split (IBBroadcast.pulse_event).
    blastedIds: [],
  }),

  getters: {
    // The fresh feed: not dismissed, not chip-suppressed, not yet blasted.
    freshEvents(state) {
      return state.events
        .filter((event) => !state.dismissedIds.includes(event.id))
        .filter((event) => !state.suppressedSources.includes(event.source))
        .filter((event) => !state.blastedIds.includes(event.id));
    },

    // The catch-up table: events blasted this session (moved over locally,
    // newest send first) ahead of the server-provided rows.
    catchUpEvents(state) {
      const serverIds = new Set(state.catchUp.events.map((event) => event.id));
      const local = state.events.filter(
        (event) => state.blastedIds.includes(event.id) && !serverIds.has(event.id),
      );
      return [...local, ...state.catchUp.events];
    },

    isCatchUp: (state, getters) => (eventId) => (
      getters.catchUpEvents.some((event) => event.id === eventId)
    ),
  },

  mutations: {
    setLoading(state, value) {
      state.loading = Boolean(value);
    },
    setError(state, value) {
      state.error = Boolean(value);
    },
    // Initial load / Refresh: replace the fresh feed from the top.
    setFresh(state, { events, has_more: hasMore } = {}) {
      state.events = events || [];
      state.freshHasMore = Boolean(hasMore);
      state.loaded = true;
      state.error = false;
    },
    // "Show previous events": older batch appended at the tail.
    appendFresh(state, { events, has_more: hasMore } = {}) {
      state.events = [...state.events, ...dedupeById(state.events, events)];
      state.freshHasMore = Boolean(hasMore);
    },
    // Live poll: newer events prepended at the head (tail untouched).
    prependFresh(state, { events } = {}) {
      state.events = [...dedupeById(state.events, events), ...state.events];
    },
    setCatchUp(state, { events, has_more: hasMore } = {}) {
      state.catchUp = { events: events || [], hasMore: Boolean(hasMore) };
    },
    appendCatchUp(state, { events, has_more: hasMore } = {}) {
      state.catchUp = {
        events: [...state.catchUp.events, ...dedupeById(state.catchUp.events, events)],
        hasMore: Boolean(hasMore),
      };
    },
    prependCatchUp(state, { events } = {}) {
      state.catchUp = {
        ...state.catchUp,
        events: [...dedupeById(state.catchUp.events, events), ...state.catchUp.events],
      };
    },
    toggleSource(state, source) {
      if (state.suppressedSources.includes(source)) {
        state.suppressedSources = state.suppressedSources.filter((s) => s !== source);
      } else {
        state.suppressedSources = [...state.suppressedSources, source];
      }
    },
    clearSourceFilter(state) {
      state.suppressedSources = [];
    },
    dismissEvent(state, eventId) {
      if (!state.dismissedIds.includes(eventId)) {
        state.dismissedIds = [...state.dismissedIds, eventId];
      }
    },
    markBlasted(state, eventId) {
      if (!state.blastedIds.includes(eventId)) {
        state.blastedIds = [...state.blastedIds, eventId];
      }
    },
  },

  actions: {
    // Initial preload of both collections (first mount and the Refresh button —
    // Refresh collapses any expanded "Show previous" back to the top).
    async fetchInitial({ commit }) {
      commit('setLoading', true);
      try {
        const { fresh, catch_up: catchUp } = await getIBPulseFeed({
          fresh_direction: 'initial',
          catch_up_direction: 'initial',
        });
        commit('setFresh', fresh);
        commit('setCatchUp', catchUp);
      } catch (error) {
        commit('setError', true);
      } finally {
        commit('setLoading', false);
      }
    },

    async loadPreviousFresh({ commit, state }) {
      if (!state.events.length || state.loading) return;
      commit('setLoading', true);
      try {
        const cursor = state.events[state.events.length - 1].created_at;
        const { fresh } = await getIBPulseFeed({
          fresh_direction: 'older',
          fresh_cursor: cursor,
        });
        commit('appendFresh', fresh);
      } catch (error) {
        commit('setError', true);
      } finally {
        commit('setLoading', false);
      }
    },

    async loadPreviousCatchUp({ commit, state }) {
      if (!state.catchUp.events.length || state.loading) return;
      commit('setLoading', true);
      try {
        const cursor = state.catchUp.events[state.catchUp.events.length - 1].last_blasted_at;
        const { catch_up: catchUp } = await getIBPulseFeed({
          catch_up_direction: 'older',
          catch_up_cursor: cursor,
        });
        commit('appendCatchUp', catchUp);
      } catch (error) {
        commit('setError', true);
      } finally {
        commit('setLoading', false);
      }
    },

    // Live head refresh (30s poll): prepend events newer than what's loaded,
    // leaving accumulated "Show previous" history intact. A collection with
    // nothing loaded yet gets an initial load instead so it can cold-start.
    async refreshHead({ commit, state }) {
      const freshDirection = state.events.length ? 'newer' : 'initial';
      const catchDirection = state.catchUp.events.length ? 'newer' : 'initial';
      const payload = {
        fresh_direction: freshDirection,
        catch_up_direction: catchDirection,
      };
      if (freshDirection === 'newer') payload.fresh_cursor = state.events[0].created_at;
      if (catchDirection === 'newer') {
        payload.catch_up_cursor = state.catchUp.events[0].last_blasted_at;
      }
      try {
        const { fresh, catch_up: catchUp } = await getIBPulseFeed(payload);
        if (fresh) commit(freshDirection === 'newer' ? 'prependFresh' : 'setFresh', fresh);
        if (catchUp) commit(catchDirection === 'newer' ? 'prependCatchUp' : 'setCatchUp', catchUp);
      } catch (error) {
        // A failed poll keeps whatever is already on screen.
      }
    },

    // Engagement counters are best-effort aggregates — the UI action (hide,
    // blast composed) must never be blocked or rolled back by a failed report.
    async recordEngagement(context, payload) {
      try {
        await recordIBPulseEngagement(payload);
      } catch (error) {
        // counters are lossy by design
      }
    },

    async dismissEvent({ commit, dispatch }, eventId) {
      commit('dismissEvent', eventId);
      await dispatch('recordEngagement', { event_id: eventId, dismiss_count: 1 });
    },
  },
};
